import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import './css/CustomerDetails.css';
import SideNav from './SideNav';
import TopRootDiv from './TopRootDiv';
import SherDineNav from './SherDineNav';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { DBContext } from './DBContext';

function CustomerDetails() {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const { logginedDB } = useContext(DBContext);
    const navigate = useNavigate();

    // Layout state
    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalseState] = useState(null);

    // Customer list state
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [dueSortOrder, setDueSortOrder] = useState('none'); // 'none' | 'desc' | 'asc'
    const limit = 10;

    // Selected customer & ledger state
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);

    // Payment Modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [paymentRemarks, setPaymentRemarks] = useState('');
    const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

    // Debounce timer ref
    const searchTimerRef = useRef(null);

    const handleSideNav = (state) => {
        setSideNav(state);
    };

    // Fetch paginated customers
    const fetchCustomers = useCallback(async (page = 1, sortOverride) => {
        if (!logginedDB || loading) return;
        setLoading(true);
        try {
            const params = { page, limit };
            const currentSort = sortOverride !== undefined ? sortOverride : dueSortOrder;
            if (currentSort !== 'none') {
                params.sortBy = 'due';
                params.sortOrder = currentSort;
            }
            const response = await axios.get(
                `${apiBaseUrl}/main/GetCustomer/${logginedDB}`,
                { params }
            );
            if (response.status === 200 && response.data.success) {
                setCustomers(response.data.data);
                setTotalCustomers(response.data.total);
                setHasMore(response.data.hasMore);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
            toast.error("Failed to load customers");
        } finally {
            setLoading(false);
        }
    }, [logginedDB, apiBaseUrl, loading, dueSortOrder]);

    // Search customers
    const searchCustomers = useCallback(async (query) => {
        if (!logginedDB) return;
        if (!query || query.trim() === '') {
            setIsSearching(false);
            fetchCustomers(1);
            return;
        }
        setIsSearching(true);
        setLoading(true);
        try {
            const response = await axios.get(
                `${apiBaseUrl}/main/GetCustomerSearch/${logginedDB}`,
                { params: { q: query } }
            );
            if (response.status === 200 && response.data.success) {
                setCustomers(response.data.data);
                setTotalCustomers(response.data.total);
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error searching customers:", error);
            toast.error("Search failed");
        } finally {
            setLoading(false);
        }
    }, [logginedDB, apiBaseUrl]);

    // Fetch ledger for selected customer
    const fetchLedger = useCallback(async (customerId) => {
        if (!logginedDB || !customerId) return;
        setLedgerLoading(true);
        try {
            const response = await axios.get(
                `${apiBaseUrl}/main/GetCustomerLedger/${logginedDB}/${customerId}`
            );
            if (response.status === 200 && response.data.success) {
                setLedgerEntries(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching ledger:", error);
            setLedgerEntries([]);
        } finally {
            setLedgerLoading(false);
        }
    }, [logginedDB, apiBaseUrl]);

    // Initial load
    useEffect(() => {
        if (logginedDB) {
            fetchCustomers(1);
        }
    }, [logginedDB]);

    // Handle search with debounce
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            searchCustomers(value);
        }, 400);
    };

    // Handle customer selection
    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        fetchLedger(customer.id);
    };

    // Pagination
    const totalPages = Math.ceil(totalCustomers / limit);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchCustomers(page);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return '₹' + parseFloat(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Format date with time
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Get transaction type badge class
    const getTypeBadge = (type) => {
        switch (type?.toUpperCase()) {
            case 'BILL': return 'badge-bill';
            case 'PAYMENT': return 'badge-payment';
            case 'ADJUSTMENT': return 'badge-adjustment';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    // Handle payment submission
    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        const amount = parseFloat(paymentAmount);
        
        if (!selectedCustomer || !amount || amount <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        const currentDue = parseFloat(selectedCustomer.due || 0);
        if (amount > currentDue) {
            toast.error("Payment cannot exceed total due amount");
            return;
        }

        setIsSubmittingPayment(true);
        const staffName = localStorage.getItem('USER') || 'Admin';

        try {
            const response = await axios.post(
                `${apiBaseUrl}/main/AddCustomerPayment/${logginedDB}`,
                {
                    customerId: selectedCustomer.id,
                    paymentAmount: amount,
                    paymentMethod: paymentMethod,
                    remarks: paymentRemarks,
                    staffName: staffName
                }
            );

            if (response.data.success) {
                toast.success("Payment successful!");
                setShowPaymentModal(false);
                setPaymentAmount('');
                setPaymentRemarks('');
                setPaymentMethod('Cash');
                
                // Refresh data
                fetchLedger(selectedCustomer.id);
                fetchCustomers(currentPage);
                
                // Update local selected customer due to reflect immediately
                setSelectedCustomer(prev => ({
                    ...prev,
                    due: response.data.newDue
                }));
            }
        } catch (error) {
            console.error("Error submitting payment:", error);
            toast.error(error.response?.data?.message || "Failed to process payment");
        } finally {
            setIsSubmittingPayment(false);
        }
    };

    // Toggle sort order: none → desc → asc → none
    const toggleDueSort = () => {
        const next = dueSortOrder === 'none' ? 'desc' : dueSortOrder === 'desc' ? 'asc' : 'none';
        setDueSortOrder(next);
        fetchCustomers(1, next);
    };

    // Calculate summary stats
    const totalDue = customers.reduce((sum, c) => sum + parseFloat(c.due || 0), 0);
    const selectedDue = selectedCustomer ? parseFloat(selectedCustomer.due || 0) : 0;

    // Ledger summary for selected customer
    const ledgerTotalDebit = ledgerEntries.reduce((sum, e) => sum + parseFloat(e.Debit || 0), 0);
    const ledgerTotalCredit = ledgerEntries.reduce((sum, e) => sum + parseFloat(e.Credit || 0), 0);

    return (
        <div className='w-screen h-screen hidden sm:flex sm:flex-col overflow-hidden'>
            <SherDineNav sideNav={(state) => handleSideNav(state)} falseState={falseState} />
            <div className='w-full bg-[#f5f5f5] flex-grow flex gap-3 overflow-hidden'>
                <div className='w-max h-full z-[9999]'>
                    <SideNav focus={'Report'} state={sideNav} falseState={(state) => setFalseState(state)} />
                </div>
                <div className='w-full h-full flex flex-col pr-3 pl-0 pb-3 pt-0 gap-3 overflow-hidden customer-page-enter'>
                    {/* Breadcrumb */}
                    <div className="pr-3 h-max w-full bg-[#fff] borderBottom">
                        <TopRootDiv image={'report'} root={['Report']} section={'Customer Details'} />
                    </div>

                    {/* Summary Stat Cards */}
                    <div className='w-full flex gap-3 px-1'>
                        <div className='customer-stat-card flex-1 bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#0008B4]'>
                            <div className='text-xs font-nunito font-semibold text-gray-400 uppercase tracking-wider'>Total Customers</div>
                            <div className='text-2xl font-nunito font-bold text-[#464255] mt-1'>{totalCustomers}</div>
                        </div>
                        <div className='customer-stat-card flex-1 bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF5B5B]'>
                            <div className='text-xs font-nunito font-semibold text-gray-400 uppercase tracking-wider'>Total Due</div>
                            <div className='text-2xl font-nunito font-bold text-[#FF5B5B] mt-1'>{formatCurrency(totalDue)}</div>
                        </div>
                        <div className={`customer-stat-card flex-1 bg-white rounded-lg p-4 shadow-sm border-l-4 ${selectedCustomer ? 'border-[#2e7d32]' : 'border-gray-300'}`}>
                            <div className='text-xs font-nunito font-semibold text-gray-400 uppercase tracking-wider'>
                                {selectedCustomer ? selectedCustomer.name : 'Select Customer'}
                            </div>
                            <div className={`text-2xl font-nunito font-bold mt-1 ${selectedCustomer ? (selectedDue > 0 ? 'text-[#FF5B5B]' : 'text-[#2e7d32]') : 'text-gray-300'}`}>
                                {selectedCustomer ? formatCurrency(selectedDue) : '₹0.00'}
                            </div>
                        </div>
                        {selectedCustomer && (
                            <div className='customer-stat-card flex-1 bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#7c4dff] customer-card-animate'>
                                <div className='text-xs font-nunito font-semibold text-gray-400 uppercase tracking-wider'>Ledger Entries</div>
                                <div className='text-2xl font-nunito font-bold text-[#7c4dff] mt-1'>{ledgerEntries.length}</div>
                            </div>
                        )}
                    </div>

                    {/* Main Content */}
                    <div className='w-full h-full flex gap-3 overflow-hidden px-1'>
                        {/* Left Panel — Customer List */}
                        <div className={`${selectedCustomer ? 'w-[45%]' : 'w-full'} h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-300`}>
                            {/* Search Bar */}
                            <div className='w-full p-3 border-b bg-[#fafafa]'>
                                <div className='relative'>
                                    <svg className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                                    </svg>
                                    <input
                                        id="customer-search-input"
                                        type='text'
                                        placeholder='Search by name, mobile, or due amount...'
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className='customer-search w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg font-nunito text-sm focus:border-[#0008B4] transition-all'
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => {
                                                setSearchQuery('');
                                                setIsSearching(false);
                                                fetchCustomers(1);
                                            }}
                                            className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm'
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                {isSearching && (
                                    <div className='mt-2 text-xs font-nunito text-gray-500'>
                                        Found {customers.length} result{customers.length !== 1 ? 's' : ''} for "{searchQuery}"
                                    </div>
                                )}
                            </div>

                            {/* Table Header */}
                            <div className='w-full flex bg-[#f0f1f5] font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider'>
                                <div className='w-[5%] p-3 text-center'>#</div>
                                <div className='w-[25%] p-3'>Name</div>
                                <div className='w-[20%] p-3'>Mobile</div>
                                <div className='w-[20%] p-3'>Address</div>
                                <div className='w-[15%] p-3'>Locality</div>
                                <div className='w-[15%] p-3 text-right cursor-pointer select-none flex items-center justify-end gap-1 hover:text-[#0008B4] transition-colors' onClick={toggleDueSort}>
                                    Due
                                    {dueSortOrder === 'none' && <span className='text-gray-300'>↕</span>}
                                    {dueSortOrder === 'desc' && <span className='text-[#0008B4]'>↓</span>}
                                    {dueSortOrder === 'asc' && <span className='text-[#0008B4]'>↑</span>}
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className='w-full flex-1 overflow-auto'>
                                {loading ? (
                                    <div className='w-full h-full flex items-center justify-center'>
                                        <div className='flex flex-col items-center gap-3'>
                                            <div className='w-8 h-8 border-3 border-[#0008B4] border-t-transparent rounded-full animate-spin'></div>
                                            <span className='font-nunito text-sm text-gray-400'>Loading customers...</span>
                                        </div>
                                    </div>
                                ) : customers.length === 0 ? (
                                    <div className='empty-state w-full h-full flex flex-col items-center justify-center gap-3 p-8'>
                                        <svg className='w-16 h-16 text-gray-200' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.5' d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' />
                                        </svg>
                                        <span className='font-nunito text-gray-400 text-sm'>
                                            {isSearching ? 'No customers match your search' : 'No customers found'}
                                        </span>
                                    </div>
                                ) : (
                                    customers.map((customer, index) => (
                                        <div
                                            key={customer.id}
                                            id={`customer-row-${customer.id}`}
                                            className={`customer-row w-full flex font-nunito text-sm border-b ${selectedCustomer?.id === customer.id ? 'active' : ''}`}
                                            onClick={() => handleSelectCustomer(customer)}
                                        >
                                            <div className='w-[5%] p-3 text-center text-gray-400 text-xs'>
                                                {isSearching ? index + 1 : (currentPage - 1) * limit + index + 1}
                                            </div>
                                            <div className='w-[25%] p-3 font-semibold text-[#464255] truncate'>
                                                {customer.name || '-'}
                                            </div>
                                            <div className='w-[20%] p-3 text-gray-600 truncate'>
                                                {customer.mobile || '-'}
                                            </div>
                                            <div className='w-[20%] p-3 text-gray-500 truncate'>
                                                {customer.address || '-'}
                                            </div>
                                            <div className='w-[15%] p-3 text-gray-500 truncate'>
                                                {customer.locality || '-'}
                                            </div>
                                            <div className={`w-[15%] p-3 text-right font-semibold ${parseFloat(customer.due || 0) > 0 ? 'text-[#FF5B5B]' : 'text-[#2e7d32]'}`}>
                                                {formatCurrency(customer.due)}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Pagination */}
                            {!isSearching && totalPages > 1 && (
                                <div className='w-full flex items-center justify-between p-3 border-t bg-[#fafafa]'>
                                    <span className='font-nunito text-xs text-gray-500'>
                                        Page {currentPage} of {totalPages} • {totalCustomers} total
                                    </span>
                                    <div className='flex gap-1'>
                                        <button
                                            className='page-btn px-3 py-1.5 rounded-md border border-gray-200 text-xs font-nunito font-semibold'
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            ← Prev
                                        </button>
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let page;
                                            if (totalPages <= 5) {
                                                page = i + 1;
                                            } else if (currentPage <= 3) {
                                                page = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                page = totalPages - 4 + i;
                                            } else {
                                                page = currentPage - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={page}
                                                    className={`page-btn px-3 py-1.5 rounded-md border border-gray-200 text-xs font-nunito font-semibold ${currentPage === page ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className='page-btn px-3 py-1.5 rounded-md border border-gray-200 text-xs font-nunito font-semibold'
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Panel — Customer Ledger */}
                        {selectedCustomer && (
                            <div className='w-[55%] h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden ledger-panel-enter'>
                                {/* Customer Info Header */}
                                <div className='w-full p-4 border-b bg-gradient-to-r from-[#f8f9ff] to-[#fff]'>
                                    <div className='flex items-center justify-between'>
                                        <div className='flex items-center gap-3'>
                                            <div className='w-10 h-10 rounded-full bg-[#0008B4] bg-opacity-10 flex items-center justify-center selected-pulse'>
                                                <span className='text-[#0008B4] font-bold font-nunito text-lg'>
                                                    {selectedCustomer.name?.charAt(0)?.toUpperCase() || '?'}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className='font-nunito font-bold text-[#464255] text-base'>
                                                    {selectedCustomer.name}
                                                </h3>
                                                <div className='flex items-center gap-3 text-xs font-nunito text-gray-400'>
                                                    <span>📱 {selectedCustomer.mobile || 'N/A'}</span>
                                                    {selectedCustomer.address && <span>📍 {selectedCustomer.address}</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            {parseFloat(selectedCustomer.due || 0) > 0 && (
                                                <button
                                                    onClick={() => setShowPaymentModal(true)}
                                                    className='bg-[#FF5B5B] hover:bg-[#ff4040] text-white px-4 py-1.5 rounded-md text-sm font-nunito font-bold transition-colors shadow-sm flex items-center gap-1'
                                                >
                                                    <span>Pay</span>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedCustomer(null);
                                                    setLedgerEntries([]);
                                                }}
                                                className='text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-all'
                                            >
                                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mini stats */}
                                    <div className='flex gap-4 mt-3'>
                                        <div className='flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-md border'>
                                            <div className='w-2 h-2 rounded-full bg-orange-400'></div>
                                            <span className='text-xs font-nunito text-gray-500'>Debit:</span>
                                            <span className='text-xs font-nunito font-bold text-orange-600'>{formatCurrency(ledgerTotalDebit)}</span>
                                        </div>
                                        <div className='flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-md border'>
                                            <div className='w-2 h-2 rounded-full bg-green-400'></div>
                                            <span className='text-xs font-nunito text-gray-500'>Credit:</span>
                                            <span className='text-xs font-nunito font-bold text-green-600'>{formatCurrency(ledgerTotalCredit)}</span>
                                        </div>
                                        <div className='flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-md border'>
                                            <div className='w-2 h-2 rounded-full bg-[#0008B4]'></div>
                                            <span className='text-xs font-nunito text-gray-500'>Balance:</span>
                                            <span className={`text-xs font-nunito font-bold ${selectedDue > 0 ? 'text-[#FF5B5B]' : 'text-[#2e7d32]'}`}>
                                                {formatCurrency(selectedDue)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Ledger Table Header */}
                                <div className='w-full flex bg-[#f0f1f5] font-nunito text-[0.7rem] font-bold text-gray-500 uppercase tracking-wider'>
                                    <div className='w-[18%] p-2.5'>Date</div>
                                    <div className='w-[12%] p-2.5'>Type</div>
                                    <div className='w-[12%] p-2.5'>Ref ID</div>
                                    <div className='w-[13%] p-2.5 text-right'>Debit</div>
                                    <div className='w-[13%] p-2.5 text-right'>Credit</div>
                                    <div className='w-[14%] p-2.5 text-right'>Balance</div>
                                    <div className='w-[18%] p-2.5'>Remarks</div>
                                </div>

                                {/* Ledger Table Body */}
                                <div className='w-full flex-1 overflow-auto ledger-scroll'>
                                    {ledgerLoading ? (
                                        <div className='w-full h-full flex items-center justify-center'>
                                            <div className='flex flex-col items-center gap-3'>
                                                <div className='w-7 h-7 border-3 border-[#7c4dff] border-t-transparent rounded-full animate-spin'></div>
                                                <span className='font-nunito text-xs text-gray-400'>Loading ledger...</span>
                                            </div>
                                        </div>
                                    ) : ledgerEntries.length === 0 ? (
                                        <div className='empty-state w-full h-full flex flex-col items-center justify-center gap-3 p-8'>
                                            <svg className='w-12 h-12 text-gray-200' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.5' d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                                            </svg>
                                            <span className='font-nunito text-gray-400 text-xs'>
                                                No ledger entries for this customer
                                            </span>
                                        </div>
                                    ) : (
                                        ledgerEntries.map((entry, index) => (
                                            <div
                                                key={entry.LedgerId || index}
                                                className='ledger-row w-full flex font-nunito text-xs border-b'
                                            >
                                                <div className='w-[18%] p-2.5 text-gray-600'>
                                                    {formatDateTime(entry.TransactionDate)}
                                                </div>
                                                <div className='w-[12%] p-2.5'>
                                                    <span className={`px-2 py-0.5 rounded-full text-[0.65rem] font-semibold ${getTypeBadge(entry.TransactionType)}`}>
                                                        {entry.TransactionType}
                                                    </span>
                                                </div>
                                                <div className='w-[12%] p-2.5 text-gray-500 font-mono'>
                                                    {entry.RefId || '-'}
                                                </div>
                                                <div className={`w-[13%] p-2.5 text-right font-semibold ${parseFloat(entry.Debit || 0) > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                                                    {parseFloat(entry.Debit || 0) > 0 ? formatCurrency(entry.Debit) : '-'}
                                                </div>
                                                <div className={`w-[13%] p-2.5 text-right font-semibold ${parseFloat(entry.Credit || 0) > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                                                    {parseFloat(entry.Credit || 0) > 0 ? formatCurrency(entry.Credit) : '-'}
                                                </div>
                                                <div className='w-[14%] p-2.5 text-right font-bold text-[#464255]'>
                                                    {formatCurrency(entry.BalanceAfter)}
                                                </div>
                                                <div className='w-[18%] p-2.5 text-gray-500 truncate' title={entry.Remarks}>
                                                    {entry.Remarks || '-'}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Payment Modal */}
            {showPaymentModal && selectedCustomer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden flex flex-col popup-enter">
                        <div className="px-5 py-4 border-b flex justify-between items-center bg-[#f8f9ff]">
                            <h2 className="font-nunito font-bold text-[#464255] text-lg">Settle Payment</h2>
                            <button 
                                onClick={() => setShowPaymentModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handlePaymentSubmit} className="p-5 flex flex-col gap-4">
                            <div className="bg-red-50 p-3 rounded-md border border-red-100 flex justify-between items-center">
                                <span className="font-nunito text-red-800 text-sm font-semibold">Total Due:</span>
                                <span className="font-nunito text-red-600 font-bold text-lg">{formatCurrency(parseFloat(selectedCustomer.due || 0))}</span>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="font-nunito text-sm font-semibold text-gray-700">Payment Amount</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                                    <input 
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max={parseFloat(selectedCustomer.due || 0)}
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0008B4] focus:border-transparent font-nunito"
                                        required
                                    />
                                </div>
                                {paymentAmount && parseFloat(paymentAmount) > parseFloat(selectedCustomer.due || 0) && (
                                    <p className="text-red-500 text-xs font-nunito mt-1">Amount cannot exceed total due</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="font-nunito text-sm font-semibold text-gray-700">Payment Method</label>
                                <select 
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0008B4] font-nunito"
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </select>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="font-nunito text-sm font-semibold text-gray-700">Remarks (Optional)</label>
                                <textarea 
                                    value={paymentRemarks}
                                    onChange={(e) => setPaymentRemarks(e.target.value)}
                                    placeholder="Enter any notes about this payment..."
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0008B4] font-nunito resize-none"
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 mt-2">
                                <button 
                                    type="button"
                                    onClick={() => setShowPaymentModal(false)}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md font-nunito font-bold text-sm transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isSubmittingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0 || parseFloat(paymentAmount) > parseFloat(selectedCustomer.due || 0)}
                                    className="px-4 py-2 bg-[#0008B4] hover:bg-[#000694] text-white rounded-md font-nunito font-bold text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isSubmittingPayment ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Processing...
                                        </>
                                    ) : 'Confirm Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CustomerDetails;
